import * as React from 'react';
import styled from 'styled-components';
import { LanguageCode, SurveyQuestion, assertNever, fieldValidation } from '../../../../amorg-common';
import { Button } from '../../../../designSystem/button/Button';
import { colors } from '../../../../designSystem/colors/color';
import { Flex } from '../../../../designSystem/compositeComponents/FlexHelpers';
import { Checkbox } from '../../../../designSystem/inputs/Checkbox';
import { TextArea } from '../../../../designSystem/inputs/TextArea';
import { P } from '../../../../designSystem/typography/Body';
import { SurveyTxt } from '../../../../SurveyTxt';
import { Icon } from '../../../../designSystem/icons/Icons';
import { InfoChip } from '../../../../designSystem/Chip';
import { QuestionProgressPanel } from './ProgressPanel';
import { SurveyOuterCard } from './SurveyCard';

export type SetQuestionSelectionProps = {
    txt:SurveyTxt,
    selectedLanguage:LanguageCode,
    absoluteQuestionIndex: number,
    question: SurveyQuestion,
    themeName: string,
    themeTranslations: { languageCode: LanguageCode, text: string }[],
    activeQuestionIndex: number,
    totalActive: number,
    isFirst: boolean,
    isCompleted: boolean,
    defaultLanguage: LanguageCode,
    hasSegmentations: boolean,
    onPreviousClicked: (absoluteQuestionIndex: number) => void,
    onSubmitSelection: (absoluteQuestionIndex: number, values: string[], comment?: string) => void
}

export function SetQuestionSelection(props: SetQuestionSelectionProps): JSX.Element {
    const [selection, setSelection] = React.useState<string[]>([]);
    const [enableCommentOverwrite, setEnableCommentOverwrite] = React.useState(false);
    const [comment, setComment] = React.useState('');

    React.useEffect(() => {
        setSelection([]);
        setComment('');
        setEnableCommentOverwrite(false);
    }, [props.absoluteQuestionIndex])

    function handleValueChange(value: string) {
        setSelection([value]);
    }

    function handleCheckboxChange(value: string, checked: boolean) {
        if(checked)
            setSelection(s => [...s, value]);
        else
            setSelection(s => s.filter(v => v !== value));
    }

    async function handleClickPrevious() {
        props.onPreviousClicked(props.absoluteQuestionIndex);
    }

    async function handleSubmitSelection() {
        if (selection.length > 0) {
            props.onSubmitSelection(
                props.absoluteQuestionIndex,
                selection.map(s => s),
                commentToSubmit
            );
        }
    }


    // ----------- Theme text --------------------------
    const localizedThemeText:string = props.selectedLanguage === props.defaultLanguage
        ? props.themeName
        // Fallback to default language if translation is not available
        : props.themeTranslations.find(t => t.languageCode === props.selectedLanguage)?.text || props.themeName;


    // ----------- Question text --------------------------
    const localizedQuestionText:string = props.selectedLanguage === props.defaultLanguage
        ? props.question.title
        // Fallback to default language if translation is not available
        : props.question.translations.find(t => t.languageCode === props.selectedLanguage)?.text || props.question.title;


    // ----------- question options --------------------------
    let values: { text: string, value: string }[] = [];
    if(props.question.type === 'default')
    {
        values = [
            { text: props.txt.setQuestionSelection_value01, value: "1" },
            { text: props.txt.setQuestionSelection_value02, value: "2" },
            { text: props.txt.setQuestionSelection_value03, value: "3" },
            { text: props.txt.setQuestionSelection_value04, value: "4" },
            { text: props.txt.setQuestionSelection_value05, value: "5" }
        ];
    }
    else if(props.question.type === 'custom')
    {
        for(const v of props.question.options)
        {
            const value = v.value.toString();
            const text = props.selectedLanguage === props.defaultLanguage
                ? v.text
                // Fallback to default language if translation is not available
                : v.translations.find(t => t.languageCode === props.selectedLanguage)?.text || v.text;
            values.push({text, value});
        }
    }
    else if(props.question.type === 'multiple-choice')
    {
        for(const v of props.question.options)
        {
            const value = v.value.toString();
            const text = props.selectedLanguage === props.defaultLanguage
                ? v.text
                // Fallback to default language if translation is not available
                : v.translations.find(t => t.languageCode === props.selectedLanguage)?.text || v.text;
            values.push({text, value});
        }
    }
    else {
        assertNever(props.question);
    }



    // If resubmission, comment field will only be submitted if checkbox has mark
    const commentToSubmit = props.isCompleted
        ? (enableCommentOverwrite ? comment : undefined)
        : comment;

    const canSubmit =
        selection.length > 0 && fieldValidation.surveyAnswer.comment(commentToSubmit)

    const isMultipleChoice = props.question.type === 'multiple-choice';

    const selectionText = props.txt.setQuestionSelection_amountSelected(selection.length);

    const totalSteps = props.totalActive + (props.hasSegmentations ? 1 : 0);
    const currentStep = props.activeQuestionIndex + (props.hasSegmentations ? 1 : 0);

    return (
        <Flex flexDirection="column" gap={24}>

            <QuestionProgressPanel 
                currentQuestionIndex={currentStep}
                questionCount={totalSteps}
                txt={props.txt}
                hasSegmentations={props.hasSegmentations} />

            <SurveyOuterCard>

                <Flex flexDirection="column" gap={8}>

                    <Flex justifyContent="space-between">
                        <ThemeText>{localizedThemeText}</ThemeText>

                        <div>
                            <InfoChip variant={selection.length === 0 ? 'outlined' : 'filled-light'}>{selectionText}</InfoChip>
                        </div>
                    </Flex>

                    {props.isCompleted &&
                        <P><i>{props.txt.setQuestionSelection_overwriteWarning}</i></P>
                    }
                
                    <QuestionWrapper>
                        <QuestionText>
                            {localizedQuestionText}
                        </QuestionText>
                    </QuestionWrapper>
                    <div style={{color:colors.text.gray.normal}}>
                        {props.question.type === 'multiple-choice'
                            ?   <>
                                    {props.txt.setQuestionSelection_multipleChoiceInstruction}
                                </>
                            :   <>
                                    {props.txt.setQuestionSelection_singleChoiceInstruction}
                                </>
                        }
                    </div>
                
                    {isMultipleChoice
                        ? <OptionCheckButtons
                            options={values.map(v => ({ text: v.text, value: v.value, checked: selection.includes(v.value) }))}
                            onChange={(value, checked) => handleCheckboxChange(value, checked)}
                        />
                        : <OptionRadioButtons 
                            options={values}
                            selectedValue={selection[0] || ''}
                            onChange={handleValueChange}
                        />
                    }

                    {props.question.canComment &&
                        <QuestionComment>
                            <Flex justifyContent='space-between'>
                                <Flex gap={4} alignItems="center">
                                    <Icon name="comment" size="x-small" color={colors.icon.active} />
                                    <CommentHeaderText>{props.txt.setQuestionSelection_commentSubHeader}</CommentHeaderText>
                                </Flex>

                                <CommentMessageText error={!fieldValidation.surveyAnswer.comment(comment)}>
                                    {fieldValidation.surveyAnswer.comment(comment)
                                        ? props.txt.setQuestionSelection_commentCharacterCount(comment.length)
                                        : props.txt.setQuestionSelection_commentCharacterCountError(comment.length)
                                    }
                                </CommentMessageText>
                            </Flex>

                            <TextArea
                                value={comment}
                                minRows={3}
                                onChange={(value) => setComment(value)}
                                disabled={props.isCompleted && !enableCommentOverwrite} />

                            {props.isCompleted &&
                                <div>
                                    <Checkbox
                                        text={props.txt.setQuestionSelection_commentOverwriteWarning}
                                        value="enableCommentOverwrite"
                                        checked={enableCommentOverwrite}
                                        onChange={(_: string, checked: boolean) => setEnableCommentOverwrite(checked)} />
                                </div>
                            }

                            
                        </QuestionComment>
                    }

                    <Flex flexDirection='row' justifyContent='flex-end' gap={16}>
                        
                        <Button variant="outlined"
                            onClick={handleClickPrevious}
                            text={props.txt.setQuestionSelection_buttonPreviousText}
                        />
                        
                        <Button variant="filled"
                            disabled={!canSubmit}
                            onClick={handleSubmitSelection}
                            text={props.activeQuestionIndex + 1 === props.totalActive 
                                ? props.txt.setQuestionSelection_buttonFinishText 
                                : props.txt.setQuestionSelection_buttonNextText
                            }
                        />
                    </Flex>
            
                </Flex>
            </SurveyOuterCard>
            
        </Flex>
    );
}



function ThemeText(props:React.PropsWithChildren): JSX.Element {
    return (
        <StyledThemeText>
            {props.children}
        </StyledThemeText>
    );
}

function OptionRadioButtons(props:{
    options: { text: string, value: string }[],
    selectedValue?: string,
    onChange: (value: string) => void,

}):JSX.Element {
    return (
        <div>
            {props.options.map(option => (
                <InputOptionLabel selected={props.selectedValue === option.value} key={option.value}>
                    <input
                        type="radio"
                        name="response"
                        value={option.value}
                        checked={props.selectedValue === option.value}
                        onChange={() => props.onChange(option.value)}
                    />
                    <OptionText>{option.text}</OptionText>
                </InputOptionLabel>
            ))}
        </div>
    );
}

function OptionCheckButtons(props:{
    options: { text: string, value: string, checked:boolean }[],
    selectedValue?: string,
    onChange: (value: string, checked: boolean) => void
}):JSX.Element {

    return (
        <div>
            {props.options.map(option => (
                <InputOptionLabel selected={option.checked} key={option.value}>
                    <input
                        type="checkbox"
                        name="response"
                        value={option.value}
                        checked={option.checked}
                        onChange={() => props.onChange(option.value, !option.checked)}
                    />
                    <OptionText>{option.text}</OptionText>
                </InputOptionLabel>
            ))}
        </div>
    );
}

const StyledThemeText = styled.h2`
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    color: ${colors.text.highlighted};
    
    &::before {
        content: "•";
        display: inline-block;
        line-height: 1;
        font: inherit;
        color: inherit;
        margin-right: 4px;
    }
`;

const QuestionComment = styled.div`

    & textarea{
        height: auto !important;
        padding: 5px;
    }
    
`

const QuestionWrapper = styled.div`
    white-space: pre-wrap;
    overflow-wrap: break-word;
    white-space: pre-wrap;
    word-wrap: break-word;
`;

const QuestionText = styled.h2`
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
`;

const OptionText = styled.span`
    font-size: 16px;
    font-weight: 500;
`;

const InputOptionLabel = styled.label<{ selected?: boolean }>`
    display: flex;
    align-items: center;
    cursor: pointer;
    margin-bottom: 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 20px;
    background-color: ${colors.button.dimmed.background};
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    border: 1px solid #e8e8e8;

    ${(props) => props.selected && `
        background-color: ${colors.button.light.background};
    `}



    input {
        margin-right: 8px;
    }
`;

const CommentHeaderText = styled.span`
    font-size: 16px;
    font-weight: 400;
    color: ${colors.text.highlighted};
`;

const CommentMessageText = styled.span<{error?: boolean}>`
    font-size: 14px;
    font-weight: 400;
    color: ${(props) => props.error ? colors.text.state.error : colors.text.normal};
`;